import { Fetch } from 'helpers/fetchWrapper';
import { useCancellablePromise } from 'helpers/promiseHandler';
import { useEffect, useState } from 'react';

const STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};
const campaignService = {
  _url: `${process.env.REACT_APP_CAMPAIGNS_API_URL}-campaigns`,
  // HOOKS
  GetCampaigns(searchQuery, refetch) {
    const [campaigns, setCampaigns] = useState({});
    const { cancellablePromise } = useCancellablePromise();
    const [campaignStatus, setCampaignStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setCampaignStatus(STATUS.LOADING);
      cancellablePromise(this.getCampaigns(searchQuery))
        .then(res => {
          setCampaigns(res);
          setCampaignStatus(STATUS.SUCCESS);
        })
        .catch(() => {
          setCampaignStatus(STATUS.ERROR);
        });
    }, [
      searchQuery?.page,
      searchQuery?.pageSize,
      searchQuery?.getAll,
      searchQuery?.status,
      searchQuery?.searchText,
      refetch,
    ]);
    return {
      campaigns_loading: campaignStatus === STATUS.LOADING,
      campaigns_error: campaignStatus === STATUS.ERROR ? campaignStatus : '',
      campaigns_data: campaigns,
    };
  },
  GetCampaignImages(products, createRewardCardAgain, editSingle) {
    const [campaignImages, setCampaignImages] = useState(null);
    const { cancellablePromise } = useCancellablePromise();
    const [campaignImagesStatus, setCampaignImagesStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setCampaignImagesStatus(STATUS.LOADING);
      cancellablePromise(this.getCampaignImages(products, editSingle))
        .then(res => {
          setCampaignImages(res);
          setCampaignImagesStatus(STATUS.SUCCESS);
        })
        .catch(() => {
          setCampaignImagesStatus(STATUS.ERROR);
        });
    }, [createRewardCardAgain]);
    return {
      campaignImages_loading: campaignImagesStatus === STATUS.LOADING,
      campaignImages_error: campaignImagesStatus === STATUS.ERROR ? campaignImagesStatus : '',
      campaignImages_data: campaignImages?.data,
    };
  },

  // apis
  async createCampaign(payload) {
    let res = await Fetch.post(`${this._url}/campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  // async activeCampaign(id) {
  //   let res = await Fetch.post(`${this._url}/active-campaigns`, id);
  //   if (res.status >= 200 && res.status < 300) {
  //     res = await res.json();
  //     return res;
  //   }
  //   const { message } = await res.json();
  //   throw new Error(message ?? 'Something went wrong');
  // },
  async getCampaigns({ page = 1, pageSize = 5, status = '', getAll = false, searchText = '' }) {
    let res = await Fetch.get(
      `${this._url}/campaign?page=${page}&itemsPerPage=${pageSize}&status=${status}&searchText=${searchText}&getAll=${getAll}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getCampaignImages(payload, editSingle) {
    let res = await Fetch.post(`${this._url}/campaign-base64-image`, editSingle ? { ...payload, editSingle } : payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async updateCampaign(id, payload) {
    let res = await Fetch.put(`${this._url}/campaign/${id}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async activeCampaign(id) {
    let res = await Fetch.put(`${this._url}/active-campaigns/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async deleteCampaign(id) {
    let res = await Fetch.patch(`${this._url}/campaign/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getBrandsForDashBoard({
    page = 1,
    pageSize = 10,
    getAll = false,
    limited = true,
    searchText = '',
    categories,
  }) {
    let res = await Fetch.post(
      `${this._url}/brands/for-dashboard?page=${page}&itemsPerPage=${pageSize}&searchText=${searchText}&getAll=${getAll}&limited=${limited}`,
      { categories },
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return {
        brands: res.map(({ brand_name, _id }) => ({ label: brand_name, value: _id })),
      };
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getCampaignForDashboard({ categories, brands }) {
    let res = await Fetch.post(`${this._url}/campaign/options`, { categories, brands });
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res.map(({ _id, title }) => ({ label: title, value: _id }));
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getCategoriesForDashBoard({ page = 1, pageSize = 10, searchText = '', brands }) {
    let res = await Fetch.post(
      `${this._url}/categories/for-dashboard?page=${page}&itemsPerPage=${pageSize}&searchText=${searchText}`,
      { brands },
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return {
        categories: res.items.map(({ category_name, _id }) => ({ label: category_name, value: _id })),
      };
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getCampaignTransaction({ camp_ids }) {
    let res = await Fetch.post(`${this._url}/campaign_transaction/for-dashboard`, { camp_ids });
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getCampaignProvince(province) {
    let res = await Fetch.get(`${this._url}/campaign-province?province=${province}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getPlastkUsersForMap() {
    let res = await Fetch.get(`${this._url}/get-plastk-users`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getWeeks({ categories = [], brands = [], campaigns = [], weeks = 'last_week', region = '' }) {
    let res = await Fetch.post(`${this._url}/get-weeks-transactions`, { weeks, categories, brands, campaigns, region });
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getMonthsRevenue({ ...rest }) {
    let res = await Fetch.post(`${this._url}/month-revenue`, {
      ...rest,
    });
    if (res) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getCampaignStores(products) {
    let res = await Fetch.post(`${this._url}/campaign-stores`, { products });
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
};
export default campaignService;
